/**
 * NotificationListContainer
 * --
 *
 */
import dayjs from 'dayjs';
import React from 'react';
import NotificationListPresenter from './NotificationListPresenter';
import { useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbAction } from 'store';
import { useGetPushHistories } from 'hooks/PushHistoryHooks';
import { useGetEmailHistories } from 'hooks/EmailHistoryHooks';
import { useGetMessageHistories } from 'hooks/MessageHistoryHooks';
import { useGetUsers } from 'hooks/UserHooks';
import { notification } from 'antd';

const TYPE_TEXT = {
  notification: '푸시알림',
  sms: '문자',
  email: '이메일',
};

/**----------------------------------------
 * [Component] NotificationListContainer
 * ----------------------------------------
 */
const NotificationListContainer = () => {
  const today = dayjs();
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const { messageType } = useParams();

  /* ===== State ===== */
  const [selectPeriod, setSelectPeriod] = React.useState(7);

  const period = React.useMemo(() => {
    const start = dayjs().subtract(selectPeriod, 'day').startOf('day').unix();

    return start;
  }, [selectPeriod]);

  /* ===== Data ===== */

  const { data: PushHistories } = useGetPushHistories(
    {
      start_date: period,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        const sortData = data
          .slice()
          .sort((a, b) => b.created_at - a.created_at);

        return sortData;
      },
      initialData: [],
      enabled: messageType === 'notification' ? true : false,
    },
  );
  const { data: EmailHistories } = useGetEmailHistories(
    {
      start_date: period,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        const sortData = data
          .slice()
          .sort((a, b) => b.created_at - a.created_at);

        return sortData;
      },
      initialData: [],
      enabled: messageType === 'email' ? true : false,
    },
  );
  const { data: MessageHistories } = useGetMessageHistories(
    {
      start_date: period,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        const sortData = data
          .slice()
          .sort((a, b) => b.created_at - a.created_at);

        return sortData;
      },
      initialData: [],
      enabled: messageType === 'sms' ? true : false,
    },
  );
  // 회원 데이터
  const { data: users } = useGetUsers(
    {
      join_tables: '["device"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    },
  );
  /* ===== Store ===== */
  const breadcrumbAction = useBreadcrumbAction();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const defaultValue = {
    MEDIA: 'ALL',
    RESULT: 'ALL',
    KEYWORD: '',
  };

  const value = {
    sms: {
      ...defaultValue,
      KEYWORD_TYPE: '수신번호',
    },
    email: {
      ...defaultValue,
      KEYWORD_TYPE: '이메일',
    },
    notification: {
      ...defaultValue,
      KEYWORD_TYPE: '수신자',
    },
  };

  const [inputValue, setInputValue] = React.useState(value[messageType]);
  const [isLoading, setIsLoading] = React.useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   *
   * @param {string} t
   * @param {string} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /* ===== HOOKS ===== */
  /**
   * NOTE: 푸시알림 내역 데이터 정제
   * --
   */
  const newHistoryData = React.useMemo(() => {
    if (!messageType) return [];

    setIsLoading(true);

    let history = [];
    const usersData = users?.map((_item) => {
      return {
        ..._item,
        push_token: _item?.device?.[0]?.push_token ?? '',
      };
    });

    switch (messageType) {
      case 'notification':
        history = PushHistories.map((_item) => {
          const { title, body, success, failure, send_date } =
            _item.push_history;
          return {
            key: _item.push_history_id,
            title: title,
            body: body,
            success,
            failure,
            total_count: success?.length + failure?.length,
            success_count: success?.length,
            failure_count: failure?.length,
            send_date: dayjs(send_date).format('YYYY.MM.DD HH:mm:ss'),
          };
        });

        break;
      case 'sms':
        history = MessageHistories.map((_item) => {
          const { PHONE, TO_NAME, UNIXTIME, RESULT, WAPINFO, MEDIA } =
            _item.message_history;
          const matchedUser = usersData?.find(
            (user) => user?.user_phone?.replace(/-/g, '') === PHONE,
          );
          return {
            key: _item.message_history_id,
            type: MEDIA, // 메시지 유형
            phone: PHONE, // 수신번호
            name: TO_NAME ?? matchedUser?.user_name ?? '비회원', // 수신자 명
            send_date: dayjs
              .unix(Number(UNIXTIME))
              .format('YYYY.MM.DD HH:mm:ss'),
            agency: WAPINFO,
            result:
              MEDIA === 'SMS'
                ? RESULT === '4100'
                  ? '성공'
                  : '실패'
                : RESULT === '6600'
                  ? '성공'
                  : '실패',
          };
        });

        break;
      case 'email':
        history = EmailHistories.map((_item) => {
          const { subject, html, success, failure, send_date } =
            _item.email_history;
          return {
            key: _item.email_history_id,
            subject,
            html,
            success,
            failure,
            send_date: dayjs(send_date).format('YYYY.MM.DD HH:mm:ss'),
            total_count: success?.length + failure?.length,
            success_count: success?.length,
            failure_count: failure?.length,
          };
        });
        break;
      default:
        break;
    }

    const sortHistory = history.sort((a, b) => b.key - a.key);

    setTimeout(() => setIsLoading(false), 1500);
    return sortHistory;
    // setData(sortHistory);
    // setFilterData(sortHistory);
  }, [messageType, PushHistories, MessageHistories, EmailHistories]);

  /**
   * 헤더 상단 Breadcrumb 설정
   * --
   */
  React.useEffect(() => {
    if (!messageType) return;

    const split = messageType.split('-');

    const pathName = [
      { title: '메시지 관리' },
      { title: `${TYPE_TEXT[split[0]]} 전송내역` },
    ];

    breadcrumbAction.push(pathName);
    // eslint-disable-next-line
  }, [messageType]);

  /* ===== RENDER ===== */
  return (
    <>
      <NotificationListPresenter
        histories={newHistoryData}
        navigate={navigate}
        onChange={handleChange}
        inputValue={inputValue}
        //
        type={messageType}
        setInputValue={setInputValue}
        period={selectPeriod}
        setSelectPeriod={setSelectPeriod}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </>
  );
};

export default NotificationListContainer;
